import axios from "axios";

let base = process.env.REACT_APP_API_URL || "http://localhost:3001";
if (!base.endsWith("/api")) {
  base = base.replace(/\/+$/, "") + "/api";
}
const API_BASE_URL = base;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const ticketsService = {
  async getTickets(params = {}) {
    const res = await api.get("/tickets", { params });
    return res.data;
  },
  async getTicket(id) {
    const res = await api.get(`/tickets/${id}`);
    return res.data;
  },
  async updateTicket(id, ticketData) {
    const res = await api.put(`/tickets/${id}`, ticketData);
    return res.data;
  },
  async toggleClientView(id, clientView) {
    // Toggle the client_view field for a ticket (matching Laravel behavior)
    const res = await api.patch(`/tickets/${id}/toggle-client-view`, { client_view: clientView });
    return res.data;
  },
  async getEditData(retryCount = 0) {
    const MAX_RETRIES = 3;
    try {
      const res = await api.get("/tickets/create");
      return res.data;
    } catch (error) {
      const isNetworkError =
        error.code === "ERR_NETWORK" ||
        error.code === "ERR_HTTP2_PROTOCOL_ERROR" ||
        error.message === "Network Error" ||
        error.name === "AxiosError";

      if (isNetworkError && retryCount < MAX_RETRIES) {
        const delay = 1000 * Math.pow(2, retryCount); // 1s, 2s, 4s
        console.log(
          `🔄 [ticketsService] getEditData retry ${retryCount + 1}/${MAX_RETRIES} in ${delay}ms...`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.getEditData(retryCount + 1);
      }
      throw error;
    }
  },
  async getTicketNotes(ticketId) {
    try {
      const res = await api.get(`/ticketsnotes/ticket/${ticketId}`);
      return res.data;
    } catch (error) {
      console.error("Error fetching ticket notes:", error);
      throw error;
    }
  },
  async getTicketNote(noteId) {
    try {
      const res = await api.get(`/ticketsnotes/${noteId}`);
      return res.data;
    } catch (error) {
      console.error("Error fetching ticket note:", error);
      throw error;
    }
  },
  async createTicketNote(noteData, files = null) {
    if (files && files.length > 0) {
      // Use FormData for file upload
      const formData = new FormData();
      formData.append("ticket_id", noteData.ticket_id);
      formData.append("description", noteData.description);
      files.forEach((file) => {
        formData.append("files", file);
      });

      const res = await api.post("/ticketsnotes", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return res.data;
    } else {
      // Regular JSON request
      const res = await api.post("/ticketsnotes", noteData);
      return res.data;
    }
  },
  async addTicketNote(ticketId, description) {
    const res = await api.post("/ticketsnotes", {
      ticket_id: ticketId,
      description: description,
    });
    return res.data;
  },
  async updateTicketNote(noteId, description) {
    const res = await api.put(`/ticketsnotes/${noteId}`, {
      description: description,
    });
    return res.data;
  },
  async deleteTicketNote(noteId) {
    const res = await api.delete(`/ticketsnotes/${noteId}`);
    return res.data;
  },
  async uploadNoteDocuments(noteId, files) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const res = await api.post(`/ticketsnotes/${noteId}/documents`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  },
  async deleteNoteDocument(noteId, documentId) {
    const res = await api.delete(
      `/documents/${documentId}?documentable_id=${noteId}&documentable_type=ticketsnotes`
    );
    return res.data;
  },
  async deleteTicket(id) {
    const res = await api.delete(`/tickets/${id}`);
    return res.data;
  },
  async getCalendarData(params = {}) {
    try {
      const res = await api.get("/tickets/calendar", { params });
      return res.data;
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      throw error;
    }
  },
  async updateTicketFromCalendar(id, data) {
    // Laravel route: POST /tickets/calendar/update/{id}
    const res = await api.post(`/tickets/calendar/update/${id}`, data);
    return res.data;
  },
  async getTicketsByProperty(propertyId, params = {}) {
    const res = await api.get(`/tickets/property/${propertyId}`, { params });
    return res.data;
  },
  async getTicketsByContractor(contractorId, params = {}) {
    const res = await api.get(`/tickets/contractor/${contractorId}`, {
      params,
    });
    return res.data;
  },
  async getTicketsByMember(memberId, params = {}) {
    const res = await api.get(`/tickets/member/${memberId}`, { params });
    return res.data;
  },
  async getCalendarOfMember(userId) {
    try {
      const res = await api.get(`/tickets/member/${userId}/calendar`);
      return res.data;
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      throw error;
    }
  },

  async getTicketsOfMemberForCalendar(userId, params = {}) {
    try {
      const res = await api.get(`/tickets/member/${userId}`, { params });
      return res.data;
    } catch (error) {
      console.error("Error fetching calendar tickets:", error);
      throw error;
    }
  },

  async searchTickets(params = {}) {
    const res = await api.get("/tickets/search", { params });
    return res.data;
  },
  async getStatusOverview() {
    try {
      const res = await api.get("/tickets/status-overview");
      return res.data;
    } catch (error) {
      console.error("Error fetching status overview:", error);
      throw error;
    }
  },
  async getUnassignedTickets(params = {}) {
    try {
      const res = await api.get("/tickets/calendar/unassigned", { params });
      return res.data;
    } catch (error) {
      console.error("Error fetching unassigned tickets:", error);
      throw error;
    }
  },
  async addUnassignedTicket(ticketData) {
    // Mock success response
    return {
      status: "success",
      message: "Ticket added to calendar successfully",
      ticket: {
        id: ticketData.id,
        title: ticketData.title,
        start: ticketData.start,
        end: ticketData.end,
        color: ticketData.color || "#006cff",
      },
    };
  },
  async removeTicketFromCalendar(ticketId) {
    // Mock success response
    return {
      status: "success",
      message: "Ticket removed from calendar successfully",
    };
  },
  async getTicketForCalendar(ticketId) {
    // Mock ticket data
    return {
      ticket: {
        id: ticketId,
        title: "Sample Ticket",
        status: "In Action",
        property: "Sample Property",
        contractor: "Sample Contractor",
        assignedMember: "Sample Member",
      },
      contractors: [
        { id: 1, name: "Plumbing Co" },
        { id: 2, name: "Electrical Co" },
        { id: 3, name: "Handyman Co" },
      ],
      contacts: [
        { id: 1, name: "John Smith" },
        { id: 2, name: "Jane Doe" },
        { id: 3, name: "Mike Johnson" },
      ],
    };
  },
  async getContractors() {
    try {
      const res = await api.get("/contractors");
      return res.data;
    } catch (error) {
      console.error("Error fetching contractors:", error);
      throw error;
    }
  },
  async getContractorContacts() {
    try {
      const res = await api.get("/contractors/contacts");
      return res.data;
    } catch (error) {
      console.error("Error fetching contractor contacts:", error);
      throw error;
    }
  },
  async getPropertyLocations() {
    try {
      const res = await api.get("/locations");
      return res.data;
    } catch (error) {
      console.error("Error fetching property locations:", error);
      throw error;
    }
  },
  async getStaffMembers() {
    try {
      const res = await api.get("/users/staff");
      return res.data;
    } catch (error) {
      console.error("Error fetching staff members:", error);
      throw error;
    }
  },

  /**
   * Calendar-specific methods for contractor contact calendar
   */

  /**
   * Update ticket booking dates (drag-and-drop functionality)
   */
  async updateBookInDates(ticketId, newDate, newStartTime, newEndTime) {
    try {
      const res = await api.post("/tickets/change/book-in-dates", {
        ticketId,
        newDate,
        newStartTime,
        newEndTime,
      });
      return res.data;
    } catch (error) {
      console.error("Error updating ticket booking dates:", error);
      throw error;
    }
  },

  /**
   * Remove ticket from calendar (soft delete or unassign)
   */
  async removeFromCalendar(ticketId, action = "unassign") {
    try {
      const res = await api.post("/tickets/calendar/ticket/destroy", {
        ticketId,
        action,
      });
      return res.data;
    } catch (error) {
      console.error("Error removing ticket from calendar:", error);
      throw error;
    }
  },

  /**
   * Add unassigned ticket to contractor contact
   */
  async addUnassignedTicketToContact(
    ticketId,
    contractorContactId,
    bookInDate,
    bookInTimeFrom,
    bookInTimeTo
  ) {
    try {
      const res = await api.post("/tickets/unassigned/add", {
        ticketId,
        contractorContactId,
        bookInDate,
        bookInTimeFrom,
        bookInTimeTo,
      });
      return res.data;
    } catch (error) {
      console.error("Error adding unassigned ticket:", error);
      throw error;
    }
  },

  /**
   * Get DataTable data for contractor contact tickets
   */
  async getContractorContactTicketsDataTable(contactId, dtParams) {
    try {
      const res = await api.post(
        `/tickets/contractor-contact-dt/${contactId}`,
        dtParams
      );
      return res.data;
    } catch (error) {
      console.error(
        "Error getting contractor contact tickets DataTable:",
        error
      );
      throw error;
    }
  },

  /**
   * Accept ticket by contractor contact
   */
  async acceptTicketByContractor(ticketId) {
    try {
      const res = await api.post(`/tickets/${ticketId}/contractors/accept`);
      return res.data;
    } catch (error) {
      console.error("Error accepting ticket:", error);
      throw error;
    }
  },

  /**
   * Decline ticket by contractor contact
   */
  async declineTicketByContractor(ticketId, reason) {
    try {
      const res = await api.post(`/tickets/${ticketId}/contractors/decline`, {
        reason,
      });
      return res.data;
    } catch (error) {
      console.error("Error declining ticket:", error);
      throw error;
    }
  },

  /**
   * Complete ticket by contractor contact
   */
  async completeTicketByContractor(ticketId, costOfWorks, completionNotes) {
    try {
      const res = await api.post(`/tickets/${ticketId}/contractors/complete`, {
        cost_of_works: costOfWorks,
        completion_notes: completionNotes,
      });
      return res.data;
    } catch (error) {
      console.error("Error completing ticket:", error);
      throw error;
    }
  },

  /**
   * Certificate upload & close: submit a certificate on a ticket and move it to
   * Contractor Completed in a single action (held for internal review).
   * @param {number} ticketId
   * @param {object} payload - { document_type_id, document_key, name, date_completed,
   *   date_expiration, date_contractor_appointment?, notes?, client_uuid?, supporting_docs? }
   */
  /**
   * Toggle whether a ticket is a certificate renewal (staff only).
   * Only renewal tickets show the certificate upload panel / enter the review queue.
   */
  async setCertificateRenewal(ticketId, isRenewal) {
    try {
      const res = await api.patch(`/tickets/${ticketId}/certificate-renewal`, {
        is_certificate_renewal: isRenewal ? 1 : 0,
      });
      return res.data;
    } catch (error) {
      console.error("Error updating certificate renewal flag:", error);
      throw error;
    }
  },

  async submitTicketCertificate(ticketId, payload) {
    try {
      const res = await api.post(`/tickets/${ticketId}/certificate`, payload);
      return res.data;
    } catch (error) {
      console.error("Error submitting ticket certificate:", error);
      throw error;
    }
  },

  /**
   * Update ticket from contractor calendar modal - Laravel exact endpoint
   * @param {number} ticketId - Ticket ID
   * @param {object} data - { book_in_day, book_in_time_from, book_in_time_to }
   */
  async updateFromContractorCalendar(ticketId, data) {
    try {
      const res = await api.post(
        `/tickets/update-from-contractor-calendar/${ticketId}`,
        data
      );
      return res.data;
    } catch (error) {
      console.error("Error updating ticket from contractor calendar:", error);
      throw error;
    }
  },
};

// Upload a document for a ticket
async function uploadTicketDocument(ticketId, file, name) {
  const formData = new FormData();
  formData.append("ticket_id", ticketId);
  formData.append("name", name);
  formData.append("file", file);

  const res = await api.post("/tickets/documents", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data;
}

export { uploadTicketDocument };

export default ticketsService;
